import type { MarketDataRecord } from '../services/marketDataService';
import type { RoleClusterMeta } from './marketClusterMeta';

export type MarketGroupingMode = 'grade' | 'cluster';

export type MarketGroup = {
  key: string;
  familyName: string;
  familyId: number | null;
  gradeLevel: number;
  /** Название для отображения в UI */
  label: string;
  roleName: string;
  mode: MarketGroupingMode;
  roleClusterId?: number;
  roleClusterName?: string;
  /** Роли кластера (для подсказки «?») */
  clusterRoleNames?: string[];
  /** Уникальные role_name в группе (режим grade) */
  gradeRoleNames?: string[];
};

export const MARKET_GROUP_COLUMN_LABEL = 'Роль';
export const MARKET_CLUSTER_COLUMN_LABEL = 'Кластер ролей';

export function getMarketGroupColumnLabel(mode: MarketGroupingMode): string {
  return mode === 'cluster' ? MARKET_CLUSTER_COLUMN_LABEL : MARKET_GROUP_COLUMN_LABEL;
}

export function clusterGroupKey(familyId: number, clusterId: number): string {
  return `cluster::${familyId}::${clusterId}`;
}

export function parseClusterGroupKey(key: string): { familyId: number; clusterId: number } | null {
  const match = /^cluster::(\d+)::(\d+)$/.exec(key);
  if (!match) return null;
  return { familyId: Number(match[1]), clusterId: Number(match[2]) };
}

export function marketGroupKey(
  record: Pick<MarketDataRecord, 'family_name' | 'grade_level'>,
): string | null {
  const familyName = String(record.family_name || '').trim();
  const gradeLevel = record.grade_level;
  if (!familyName || gradeLevel == null || !Number.isFinite(gradeLevel)) return null;
  return `${familyName.toLowerCase()}::${gradeLevel}`;
}

function recordMatchesFamily(
  record: Pick<MarketDataRecord, 'family_id' | 'family_name'>,
  meta: Pick<RoleClusterMeta, 'familyId' | 'familyName'>,
): boolean {
  if (record.family_id != null && Number.isFinite(Number(record.family_id))) {
    return Number(record.family_id) === meta.familyId;
  }
  return String(record.family_name || '').trim().toLowerCase() === meta.familyName.toLowerCase();
}

export function recordMatchesClusterMeta(
  record: Pick<MarketDataRecord, 'family_id' | 'family_name' | 'grade_level'>,
  meta: RoleClusterMeta,
): boolean {
  const gradeLevel = record.grade_level;
  if (!recordMatchesFamily(record, meta)) return false;
  if (gradeLevel == null || !Number.isFinite(gradeLevel)) return false;
  return gradeLevel >= meta.minGrade && gradeLevel <= meta.maxGrade;
}

function pickGroupRoleLabel(records: MarketDataRecord[]): string {
  const counts = new Map<string, number>();
  for (const record of records) {
    const name = String(record.role_name || '').trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  if (counts.size === 0) return '';
  const sortedNames = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ru'))
    .map(([name]) => name);
  return sortedNames.join(' / ');
}

export function marketGroupDisplayLabel(
  record: Pick<MarketDataRecord, 'role_name' | 'family_name' | 'grade_level'>,
): string {
  const roleName = String(record.role_name || '').trim();
  if (roleName) return roleName;
  const familyName = String(record.family_name || '').trim();
  const gradeLevel = record.grade_level;
  if (familyName && gradeLevel != null && Number.isFinite(gradeLevel)) {
    return `G${gradeLevel}`;
  }
  return '—';
}

export function filterRecordsWithMarketGroup(
  records: MarketDataRecord[],
  mode: MarketGroupingMode = 'grade',
  clusterMetas: RoleClusterMeta[] = [],
): MarketDataRecord[] {
  if (mode === 'grade') {
    return records.filter((record) => marketGroupKey(record) != null);
  }
  if (clusterMetas.length === 0) return [];
  return records.filter((record) =>
    clusterMetas.some((meta) => recordMatchesClusterMeta(record, meta)),
  );
}

function getSortedGradeMarketGroups(records: MarketDataRecord[]): MarketGroup[] {
  const byKey = new Map<string, MarketDataRecord[]>();

  for (const record of records) {
    const key = marketGroupKey(record);
    if (!key) continue;
    const list = byKey.get(key);
    if (list) list.push(record);
    else byKey.set(key, [record]);
  }

  const groups: MarketGroup[] = [];
  for (const [key, groupRecords] of Array.from(byKey.entries())) {
    const first = groupRecords[0];
    const roleLabel = pickGroupRoleLabel(groupRecords);
    const gradeRoleNames = Array.from(
      new Set(
        groupRecords
          .map((record) => String(record.role_name || '').trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b, 'ru'));
    groups.push({
      key,
      familyName: String(first.family_name).trim(),
      familyId: first.family_id ?? null,
      gradeLevel: Number(first.grade_level),
      roleName: roleLabel,
      label: roleLabel || marketGroupDisplayLabel(first),
      mode: 'grade',
      gradeRoleNames: gradeRoleNames.length > 0 ? gradeRoleNames : undefined,
    });
  }

  return groups.sort((a, b) => {
    const byFamily = a.familyName.localeCompare(b.familyName, 'ru');
    if (byFamily !== 0) return byFamily;
    if (a.gradeLevel !== b.gradeLevel) return a.gradeLevel - b.gradeLevel;
    return a.label.localeCompare(b.label, 'ru');
  });
}

function getSortedClusterMarketGroups(
  records: MarketDataRecord[],
  clusterMetas: RoleClusterMeta[],
): MarketGroup[] {
  const groups: MarketGroup[] = [];

  for (const meta of clusterMetas) {
    const groupRecords = records.filter((record) => recordMatchesClusterMeta(record, meta));
    if (groupRecords.length === 0) continue;

    groups.push({
      key: clusterGroupKey(meta.familyId, meta.clusterId),
      familyName: meta.familyName,
      familyId: meta.familyId,
      gradeLevel: meta.minGrade,
      roleName: '',
      label: meta.clusterName,
      mode: 'cluster',
      roleClusterId: meta.clusterId,
      roleClusterName: meta.clusterName,
      clusterRoleNames: meta.roleNames,
    });
  }

  return groups;
}

export function getSortedMarketGroups(
  records: MarketDataRecord[],
  mode: MarketGroupingMode = 'grade',
  clusterMetas: RoleClusterMeta[] = [],
): MarketGroup[] {
  if (mode === 'cluster') {
    return getSortedClusterMarketGroups(records, clusterMetas);
  }
  return getSortedGradeMarketGroups(records);
}

export function filterRecordsByMarketGroup(
  records: MarketDataRecord[],
  groupKey: string,
  mode: MarketGroupingMode = 'grade',
  clusterMetas: RoleClusterMeta[] = [],
): MarketDataRecord[] {
  if (mode === 'grade') {
    return records.filter((record) => marketGroupKey(record) === groupKey);
  }

  const parsed = parseClusterGroupKey(groupKey);
  if (!parsed) return [];

  const meta = clusterMetas.find(
    (item) => item.clusterId === parsed.clusterId && item.familyId === parsed.familyId,
  );
  if (!meta) return [];

  return records.filter((record) => recordMatchesClusterMeta(record, meta));
}

export function countRecordsWithoutMarketGroup(records: MarketDataRecord[]): number {
  return records.filter((record) => marketGroupKey(record) == null).length;
}
